/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The one numeric text input in the console.
 *
 * Every numeric field here was written as `value={form.n}` +
 * `onChange={(e) => set(Number(e.target.value))}`, and that pair has a defect the
 * operator hits on the very first edit: `Number('')` is `0`, so clearing the box writes
 * `0` back into state, React re-renders the box as `"0"`, and the field can no longer
 * be emptied. To type `3000` over `2000` you must select-all first — backspacing leaves
 * a `0` that will not delete. It has been reported more than once, on more than one
 * field, and patching one site at a time is what let it keep coming back.
 *
 * The fix is to stop round-tripping the *text* through a number. This component owns
 * the draft string the operator is typing and reports `undefined` while it is empty, so
 * an empty box stays empty. Intermediate states a parser would mangle — `-`, `1.`, `1e`
 * — survive for the same reason: the draft is what renders, the parse is only what is
 * reported.
 *
 * The parent still owns the value. When it changes the number (a preset fill, a reset,
 * a clamp) the draft re-syncs, so the box is never stale — but it re-syncs only when
 * the parent's number differs from what the draft already means, or `2000` would snap
 * back the instant someone typed `2000.`.
 *
 * **A field using this must accept `undefined`** and say so in its own validation
 * ("Timeout is required"). Coercing `undefined` to a default inside the control would
 * reintroduce the bug one layer down: the operator clears the box, something writes
 * 2000, and the box refills itself.
 */

import React from 'react';
import { Input, type InputProps } from '@chakra-ui/react';
import { numberToDraft, parseNumberDraft, settleDraft, shouldResyncDraft } from './NumberField.helpers';

export interface NumberFieldProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  /** The current value. `undefined` renders an empty box. */
  value: number | undefined;
  /** Called with the parsed value, or `undefined` when the box is empty. */
  onChange: (value: number | undefined) => void;
  /**
   * **The fewest decimal places to show** — `2` for money, `null` for a count or a percent.
   *
   * Required and nullable (`#1134`), because forgetting it is invisible: a price field without it
   * works, saves and round-trips, and shows `8` for `8.00` every time it is drawn (`3c-ii`). Applied
   * when a value is drawn and when the operator leaves the box, never while they type, and it only
   * ever pads: `8.125` stays `8.125`.
   */
  minDecimals: number | null;
}

export const NumberField: React.FC<NumberFieldProps> = ({ value, onChange, minDecimals, onBlur, ...rest }) => {
  const [draft, setDraft] = React.useState<string>(() => numberToDraft(value, minDecimals));

  // Re-sync only on a real disagreement — see `shouldResyncDraft`.
  React.useEffect(() => {
    setDraft((current) => (shouldResyncDraft(current, value) ? numberToDraft(value, minDecimals) : current));
  }, [value, minDecimals]);

  return (
    <Input
      {...rest}
      type="number"
      value={draft}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        onChange(parseNumberDraft(next));
      }}
      onBlur={(e) => {
        setDraft((current) => settleDraft(current, minDecimals));
        onBlur?.(e);
      }}
    />
  );
};
